import { roundMoney, sumMoney } from "./money.mjs";

const getAdjustmentAmount = (adjustment, baseTotal, currency = "EUR") => {
  const amountValue = Number(adjustment?.amount) || 0;
  if (!amountValue) {
    return 0;
  }
  if (adjustment.type === "percent") {
    return roundMoney((baseTotal * amountValue) / 100, currency);
  }
  return roundMoney(amountValue, currency);
};

const getAdjustmentDirection = (value) =>
  value === "add" ? "add" : "subtract";

const summarizeAdjustments = (adjustments, baseTotal, currency = "EUR") => {
  if (!Array.isArray(adjustments)) {
    return { adjustmentsTotal: 0, items: [] };
  }
  return adjustments.reduce(
    (acc, adjustment) => {
      const amount = getAdjustmentAmount(adjustment, baseTotal, currency);
      if (!amount) {
        return acc;
      }
      const direction = getAdjustmentDirection(adjustment.direction);
      const sign = direction === "add" ? "+" : "-";
      acc.adjustmentsTotal = roundMoney(
        acc.adjustmentsTotal + (direction === "add" ? amount : -amount),
        currency,
      );
      acc.items.push({ ...adjustment, direction, amountValue: amount, sign });
      return acc;
    },
    { adjustmentsTotal: 0, items: [] }
  );
};

const getTaxMode = (value) =>
  value === "exclusive" ? "exclusive" : "inclusive";

const getTaxTreatment = (value) => {
  if (value === "included" || value === "withheld") {
    return value;
  }
  return "added";
};

const normalizeTaxLines = (invoiceData = {}) => {
  if (Array.isArray(invoiceData.taxLines)) {
    return invoiceData.taxLines
      .map((line, index) => ({
        ...line,
        id: line?.id || `tax-${index + 1}`,
        label: String(line?.label || "Tax").trim() || "Tax",
        rate: Number(line?.rate) || 0,
        treatment: getTaxTreatment(line?.treatment),
      }))
      .filter((line) => line.enabled !== false && line.rate > 0 && line.rate <= 100);
  }

  const legacyRate = Number(invoiceData?.taxRate) || 0;
  if (!invoiceData?.taxEnabled || legacyRate <= 0) {
    return [];
  }

  return [{
    id: "legacy-tax",
    label: String(invoiceData.taxLabel || "Tax").trim() || "Tax",
    rate: legacyRate,
    treatment: getTaxMode(invoiceData.taxMode) === "inclusive" ? "included" : "added",
  }];
};

const calculateInvoiceTotals = (items, invoiceData) => {
  const currency = invoiceData?.currency || "EUR";
  const lineItems = (items || []).map((item) => ({
    ...item,
    amount: roundMoney(
      Number(item?.quantity || 0) * Number(item?.rate || 0),
      currency,
    ),
  }));
  const lineTotal = sumMoney(lineItems.map((item) => item.amount), currency);
  const { adjustmentsTotal, items: adjustmentItems } = summarizeAdjustments(
    invoiceData?.adjustments,
    lineTotal,
    currency,
  );
  const taxLines = normalizeTaxLines(invoiceData);
  const includedRate = taxLines
    .filter((line) => line.treatment === "included")
    .reduce((sum, line) => sum + line.rate, 0);
  const subtotal = includedRate > 0
    ? roundMoney(lineTotal / (1 + includedRate / 100), currency)
    : lineTotal;
  const includedLines = taxLines.filter((line) => line.treatment === "included");
  const includedTarget = roundMoney(lineTotal - subtotal, currency);
  let includedAllocated = 0;
  const taxItems = taxLines.map((line) => {
    let amount;
    if (line.treatment === "included" && line.id === includedLines[includedLines.length - 1]?.id) {
      amount = roundMoney(includedTarget - includedAllocated, currency);
    } else {
      amount = roundMoney(subtotal * (line.rate / 100), currency);
      if (line.treatment === "included") includedAllocated = roundMoney(includedAllocated + amount, currency);
    }
    return { ...line, amount };
  });
  const includedTaxTotal = sumMoney(taxItems.filter((line) => line.treatment === "included").map((line) => line.amount), currency);
  const addedTaxTotal = sumMoney(taxItems.filter((line) => line.treatment === "added").map((line) => line.amount), currency);
  const withheldTaxTotal = sumMoney(taxItems.filter((line) => line.treatment === "withheld").map((line) => line.amount), currency);
  const taxTotal = roundMoney(includedTaxTotal + addedTaxTotal, currency);
  const total = roundMoney(lineTotal + addedTaxTotal - withheldTaxTotal + adjustmentsTotal, currency);

  return {
    currency,
    lineItems,
    lineTotal,
    adjustmentsTotal,
    adjustmentItems,
    taxLines,
    taxItems,
    taxTotal,
    includedTaxTotal,
    addedTaxTotal,
    withheldTaxTotal,
    subtotal,
    total,
  };
};

const deriveLineTotalFromInvoiceTotal = (totalValue, invoiceData = {}) => {
  const currency = invoiceData?.currency || "EUR";
  const total = roundMoney(totalValue, currency);
  const taxLines = normalizeTaxLines(invoiceData);
  const rates = taxLines.reduce(
    (acc, line) => ({ ...acc, [line.treatment]: acc[line.treatment] + line.rate }),
    { added: 0, included: 0, withheld: 0 }
  );
  const adjustments = Array.isArray(invoiceData.adjustments) ? invoiceData.adjustments : [];
  const adjustmentRates = adjustments
    .filter((adjustment) => adjustment.type === "percent")
    .reduce((sum, adjustment) => sum + (getAdjustmentDirection(adjustment.direction) === "add" ? 1 : -1) * (Number(adjustment.amount) || 0), 0);
  const fixedAdjustments = adjustments
    .filter((adjustment) => adjustment.type !== "percent")
    .reduce((sum, adjustment) => sum + (getAdjustmentDirection(adjustment.direction) === "add" ? 1 : -1) * (Number(adjustment.amount) || 0), 0);
  const totalFactor = 1
    + (rates.included + rates.added - rates.withheld) / 100
    + ((1 + rates.included / 100) * adjustmentRates) / 100;
  if (totalFactor <= 0) {
    return total;
  }
  const taxableSubtotal = (total - fixedAdjustments) / totalFactor;
  return roundMoney(taxableSubtotal * (1 + rates.included / 100), currency);
};

export {
  calculateInvoiceTotals,
  deriveLineTotalFromInvoiceTotal,
  getAdjustmentAmount,
  getAdjustmentDirection,
  getTaxMode,
  getTaxTreatment,
  normalizeTaxLines,
  summarizeAdjustments,
};
